import { ActionRoutes } from '@/types';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const FOUNDED_YEAR = 2011;
const FOUNDED_MONTH = 9; // September, 1-indexed — the academy's anniversary month.

/**
 * Whole years the academy has been running, counted from its September 2011
 * founding. Ticks over on the anniversary month rather than the calendar
 * year, so the figure holds from September through the following August.
 *
 * @param {Date} [now] - Defaults to the current date; overridable for tests.
 * @return {number} Years of operation.
 */
export function yearsSinceFounding(now: Date = new Date()): number {
  const years = now.getFullYear() - FOUNDED_YEAR;
  return now.getMonth() + 1 >= FOUNDED_MONTH ? years : years - 1;
}

const resolveRoute = (routeName: string): string | undefined =>
  route().has(routeName) ? routeName : undefined;

// Function to resolve action routes (edit, delete, view)
export const resolveActionRoutes = (
  baseRoute: string
): ActionRoutes | undefined => {
  const routes = {
    editRoute: resolveRoute(`${baseRoute}.edit`),
    deleteRoute: resolveRoute(`${baseRoute}.destroy`),
    viewRoute: resolveRoute(`${baseRoute}.view`),
  };

  return Object.values(routes).some(Boolean) ? routes : undefined;
};

export function CalculateAvgRating(ratingCount: number, reviewCount: number) {
  return reviewCount !== 0 ? (ratingCount / reviewCount).toFixed(1) : 0;
}

export function formatReviewCount(reviewCount: number) {
  return reviewCount > 999
    ? (reviewCount / 1000).toFixed(1) + 'k'
    : reviewCount;
}

export function calculatePercentOff(
  originalPrice: number,
  discountedPrice: number
) {
  // Calculate percentage discount and round down to the nearest whole number
  const percentOff = ((originalPrice - discountedPrice) / originalPrice) * 100;

  return Math.floor(percentOff) + '%';
}

/**
 * Groups an array of objects by specified properties.
 * @param data The array of objects to group.
 * @param props An array of property keys to group by.
 * @returns An object representing the grouped data.
 */
export function groupBy<T extends Record<keyof T, string | number>>(
  data: T[],
  props: (keyof T)[]
) {
  const result: Record<string | number, unknown> = {};

  data.forEach((item) => {
    let current = result;

    props.forEach((prop) => {
      const key = item[prop];
      current = current[key] || (current[key] = {});
    });

    // Assign 'id' property if exists and not already assigned
    if ('id' in item && !('id' in current)) {
      current.id = item.id;
    }
  });

  return result;
}

export function groupByCategory<T extends Record<string, unknown>>(
  data: T[],
  props: (keyof T)[]
) {
  const result: Record<string | number, unknown> = {};

  data?.forEach((item) => {
    let current = result;

    props.forEach((prop) => {
      const key = item[prop];
      if (typeof key === 'string' || typeof key === 'number') {
        current = current[key] || (current[key] = {});
      }
    });

    // Assign 'sub_categories' property if exists
    if ('sub_categories' in item) {
      current.sub_categories = (item.sub_categories as unknown[]).slice();
    }

    // Assign 'description' property if exists
    if ('description' in item) {
      current.description = item.description;
    }
  });

  return result;
}

/**
 * Finds the images present in array A but not in array B.
 *
 * @param {string[] | string | undefined} a - The array of images from which to find removed images.
 * @param {string[]} b - The array of images to compare against.
 * @return {string[]} - An array of removed images.
 */
export function findRemovedImages(
  a: string[] | string | undefined,
  b: string[]
) {
  // Convert `a` to an array if it's not already an array or undefined.
  const arrayA = a !== undefined ? (Array.isArray(a) ? a : [a]) : [];

  // Convert the second list to a Set for efficient lookups.
  const setB = new Set(b);

  // Filter `arrayA` to include only the images that are not present in `setB`.
  return arrayA.filter((item) => !setB.has(item));
}

/**
 * Where an "enquire" control should point.
 *
 * The enquiry form lives on /contact and, since the home page gained a closing
 * band that carries it, on / as well. On those two pages an enquire control
 * scrolls to the form already on screen; anywhere else it navigates to the one
 * on /contact.
 *
 * A returned '#inquiry' must be rendered as a plain <a>, not an Inertia <Link>,
 * which would treat a bare hash as a visit.
 *
 * @return {string} The href for an enquire control on the current page.
 */
export function inquiryHref() {
  return route().current('home') || route().current('contact')
    ? '#inquiry'
    : '/contact#inquiry';
}

const WHATSAPP_DEFAULT_MESSAGE =
  "Hi, I'm looking for a course at Samuthrika Academy. Could you share more details?";

/**
 * Builds a click-to-chat WhatsApp link for the site's advertised number.
 *
 * `phone` is stripped of anything but digits — it already renders clean
 * everywhere else, but it's admin-entered data, so this stays defensive.
 *
 * @param {string} phone The advertised number, e.g. from CategoryContext.
 * @param {string} [message] Pre-filled chat text; defaults to a course enquiry,
 *   since courses are the main thing people message the academy about.
 * @return {string} A `https://wa.me/...` URL.
 */
export function whatsAppHref(phone: string, message?: string): string {
  const digits = phone.replace(/\D/g, '');
  return `https://wa.me/91${digits}?text=${encodeURIComponent(message ?? WHATSAPP_DEFAULT_MESSAGE)}`;
}

/**
 * Groups items by a derived key, preserving the order each key first appears.
 *
 * The About timelines (awards, Rotary roles) are already sorted newest-first in
 * their source arrays, so a Map keyed in insertion order keeps that sequence
 * without imposing a sort the data does not carry — years like "2024-25" would
 * not sort numerically anyway.
 *
 * @param {T[]} items - The list to group.
 * @param {(item: T) => string} key - Derives the group key for an item.
 * @return {Map<string, T[]>} Groups in first-appearance order.
 */
export function groupInOrder<T>(items: T[], key: (item: T) => string) {
  return items.reduce<Map<string, T[]>>((groups, item) => {
    const k = key(item);
    const existing = groups.get(k);

    if (existing) {
      existing.push(item);
    } else {
      groups.set(k, [item]);
    }

    return groups;
  }, new Map());
}
